// src/components/strumenti/IscFusibileFV.js
import React, { useState } from "react";
import { RefreshCw } from "lucide-react";
import { leggiNumero } from "../../utils/numeriStrumenti";
import { protezioneStringheFV } from "../../utils/calcoliStrumenti";

const inp = { padding:"10px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:14, fontFamily:"'Inter',sans-serif", outline:"none", width:"100%" };
const lbl = { fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 };
const FATT_SICUREZZA = 1.25; // CEI 82-25 / IEC 60364-7-712

export default function IscFusibileFV() {
  const [isc, setIsc] = useState("");
  const [stringheParallelo, setStringheParallelo] = useState("1");
  const [irmMax, setIrmMax] = useState("");

  const Isc = leggiNumero(isc)||0;
  const nStringhe = parseInt(stringheParallelo)||1;
  const iscCorretta = Isc*FATT_SICUREZZA;

  const fusibileMin = iscCorretta*1.4;
  const fusibileMax = iscCorretta*2;
  const irm = leggiNumero(irmMax)||null;
  const fusibileMaxEffettivo = irm ? Math.min(fusibileMax, irm) : fusibileMax;

  const iscTotaleParallelo = Isc*nStringhe;
  // W442: verifica CEI 64-8 sez. 712 (serve la protezione? In ammessa) — si aggiunge al range di prassi
  const cei = protezioneStringheFV(Isc, nStringhe, irm);
  const f2 = (n) => n.toLocaleString("it-IT", { maximumFractionDigits:2 });

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
        <div>
          <label style={lbl}>Isc pannello a STC (A, da datasheet)</label>
          <input value={isc} onChange={e=>setIsc(e.target.value)} style={inp} placeholder="Es. 11.2"/>
        </div>
        <div>
          <label style={lbl}>N° stringhe in parallelo su questo MPPT</label>
          <input value={stringheParallelo} onChange={e=>setStringheParallelo(e.target.value)} style={inp}/>
        </div>
      </div>
      <div style={{ marginBottom:16 }}>
        <label style={lbl}>Massima corrente inversa / fusibile max in serie del modulo (A, IMOD_MAX_OCPR) — facoltativo, da datasheet</label>
        <input value={irmMax} onChange={e=>setIrmMax(e.target.value)} style={inp} placeholder="Es. 20"/>
      </div>

      <div style={{ background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:10, padding:"14px 16px" }}>
        <div style={{ display:"flex", justifyContent:"space-around", marginBottom:10, fontSize:11, color:"#6b7280" }}>
          <div style={{textAlign:"center"}}>
            <div>Isc corretta (×1.25)</div>
            <div style={{fontWeight:700, color:"#374151", fontSize:14}}>{Isc>0 ? iscCorretta.toFixed(2) : "—"} A</div>
          </div>
          <div style={{textAlign:"center"}}>
            <div>Isc totale in parallelo</div>
            <div style={{fontWeight:700, color:"#374151", fontSize:14}}>{Isc>0 ? iscTotaleParallelo.toFixed(2) : "—"} A</div>
          </div>
        </div>
        <div style={{ textAlign:"center", borderTop:"1px solid #fed7aa", paddingTop:10 }}>
          <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, marginBottom:2 }}>Fusibile stringa consigliato (In)</div>
          <div style={{ fontSize:22, fontWeight:800, color:"#c2410c" }}>
            {Isc>0 ? `${fusibileMin.toFixed(1)} – ${fusibileMaxEffettivo.toFixed(1)} A` : "—"}
          </div>
          {irm && fusibileMax>irm && (
            <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>Limitato dalla Irm massima del pannello ({irm} A)</div>
          )}
        </div>
      </div>
      {cei && (
        <div style={{ background:"#fff", border:"1px solid #fed7aa", borderRadius:10, padding:"10px 14px", marginTop:10, fontSize:12, color:"#374151", lineHeight:1.6 }}>
          <div style={{ fontWeight:700, color:"#c2410c", marginBottom:4 }}>Verifica CEI 64-8 sez. 712</div>
          <div>Isc max = 1,25 × Isc = <strong>{f2(cei.iscMax)} A</strong> · corrente inversa possibile (Np − 1) × Isc max = <strong>{f2(cei.correnteInversa)} A</strong></div>
          {cei.serve === null && <div style={{ color:"#6b7280" }}>Inserisci la corrente IMOD_MAX_OCPR del modulo per sapere se la protezione delle stringhe è necessaria.</div>}
          {cei.serve === false && <div>(Np − 1) × Isc max ≤ 1,35 × IMOD_MAX_OCPR ({f2(1.35*cei.inMax)} A): <strong>protezione di stringa non necessaria</strong> per sovracorrente.</div>}
          {cei.serve === true && <div>(Np − 1) × Isc max &gt; 1,35 × IMOD_MAX_OCPR ({f2(1.35*cei.inMax)} A): <strong>protezione di stringa necessaria</strong>, con In tra {f2(cei.inMin)} A (1,1 × Isc max) e {f2(cei.inMax)} A.{!cei.possibile && <span style={{ color:"#991b1b" }}> Attenzione: 1,1 × Isc max supera IMOD_MAX_OCPR, nessun valore soddisfa entrambe le condizioni — verificare con il produttore.</span>}</div>}
        </div>
      )}
      <div style={{ fontSize:10, color:"#9ca3af", marginTop:8, textAlign:"center" }}>
        Range indicativo secondo prassi comune (1,4–2× Isc corretta) — verificare sempre anche la Irm massima dichiarata dal produttore del pannello.
      </div>

      <button onClick={()=>{setIsc("");setStringheParallelo("1");setIrmMax("");}}
        style={{ marginTop:12, display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#9ca3af", background:"none", border:"none", cursor:"pointer" }}>
        <RefreshCw size={12}/> Azzera
      </button>
    </div>
  );
}
